import { Dispatch } from 'redux';
import { store } from '@/store/store';
import { UserLangProgress } from '@/components/Types';
import { setLevelUpdateJustHappened, setProgressLevelUpdatePending, updateLanguageProgressStats } from '@/store/reducers/appSlice';
import { frontendApi } from '../api-requests';

interface XpGainedEvent {
    language: string;
    xpGained: number;
    xpBefore: number;
    timestamp: number;
}

/**
 * This service provides a buffer to accumulate experience gained events, that can 
 * happen in a short period of time to send them all to server at once.
 */
export class ProgressService {    
    private static instance: ProgressService;

    currentDispatch: Dispatch | null;

    pendingUpdates: XpGainedEvent[] = [];

    pendingUpdateTimer: NodeJS.Timeout | null;
    pendingUpdateScheduledAt: number | null;    

    isUpdating: boolean = false;

    public static getInstance() {
        if (!ProgressService.instance) {
            ProgressService.instance = new ProgressService();
        }
        return ProgressService.instance;
    }

    public setDispatch(dispatch: Dispatch) {
        this.currentDispatch = dispatch;
    }

    public reschedulePendingUpdate(delay: number = 5000) {
        if (this.pendingUpdateTimer) {
            clearTimeout(this.pendingUpdateTimer);
            this.pendingUpdateTimer = null;
            this.pendingUpdateScheduledAt = null;
        }

        // Schedule a pending update if there is not one already        
        this.pendingUpdateTimer = setTimeout(() => {
            this.processPendingUpdates();
        }, delay);
        this.pendingUpdateScheduledAt = Date.now() + delay;
    }

    public addXpGainedEvent(dispatch: Dispatch, language: string, xpGained: number, timestamp: number) {        
        this.currentDispatch = dispatch;
        
        const xpBefore = store.getState().app.languageProgress?.experience ?? 0;
        const tillNextLevel = store.getState().app.languageProgress?.experienceTillNextLevel ?? 0;

        const xpAfter = xpBefore + xpGained;

        if (xpAfter >= tillNextLevel) {
            dispatch(setProgressLevelUpdatePending(true));

            if (!this.isUpdating) {
                this.reschedulePendingUpdate(100);
            } else {
                // If we are already updating, schedule a longer delay
                this.reschedulePendingUpdate(1000);
            }
        } else {
            this.reschedulePendingUpdate(5000);
        }

        // Immediately update the store with new values
        dispatch(updateLanguageProgressStats({
            experience: xpAfter,
        }));
        
        this.pendingUpdates.push({ language, xpGained, xpBefore, timestamp });        

    }    

    public async processPendingUpdates() {
        // console.log('🔵 Processing pending XP updates...');
        
        this.isUpdating = true;

        let payload = {
            language: store.getState().app.languageProgress?.language ?? '',
            amount: 0,
            checksum: this.getChecksum('.'),
        }

        let numUpdates = this.pendingUpdates.length;

        for (const event of this.pendingUpdates) {
            payload.amount += event.xpGained;            
        }

        payload.checksum = this.getChecksum(payload.amount.toString());

        try {
            const result = await frontendApi.user.createUserLangProgressEvent(payload);
    
            if (result.success) {
                this.pendingUpdates = [];
                // console.log('✅ Pending XP updates processed: ', numUpdates);
            }

            // Compare the server last xp with the local store xp. 
            // Update store xp only if the server value is greater.
            const currentXp = store.getState().app.languageProgress?.experience ?? 0;
            const serverXp = result.xpAfter ?? 0;

            if (serverXp > currentXp && this.currentDispatch) {
                try {
                    this.currentDispatch(updateLanguageProgressStats({
                        experience: serverXp,
                    }));
                } catch (error) {
                    console.error('Error updating language progress stats:', error);
                }
            }

            // If a level-up was pending before the update, reset the flag
            // and indicate that a level-up just happened
            if (store.getState().app.progressLevelUpdatePending) {
                this.currentDispatch(setProgressLevelUpdatePending(false));
                this.currentDispatch(setLevelUpdateJustHappened(true));

                // Update rank and next level stats upon level-up
                if (result.newRankReached && result.rankLevel) {
                    this.currentDispatch(updateLanguageProgressStats({
                        rankLevel: result.rankLevel,
                        experienceTillNextLevel: result.xpTillNextLevel,
                    }));
                }
            }
        } catch (error) {
            console.error('Error creating user lang progress event:', error);
            // Should we throw here?
            // throw error;
        } finally {
            this.isUpdating = false;
        }
    }

    public onServerProgressUpdate(progressUpdate: any) {
        // So far we don't need to actually handle this, since it's handled in ServerModalsProvider
    }

    private getChecksum(str: string): string {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }
}

export default ProgressService;